import React, { useState } from 'react';
import "./SecurityBentoGrid.css";
import { BentoGridModal, CardData } from '../BentoGridModal/BentoGridModal';
import { ChevronRight } from 'lucide-react';
import SpeedInteractive from '../RecruitingBentoGrid/RecruitingBentoComponents/SpeedInteractive';
import MoneyInteractive from '../RecruitingBentoGrid/RecruitingBentoComponents/MoneyInteractive';
import ShieldInteractive from '../RecruitingBentoGrid/RecruitingBentoComponents/ShieldInteractive';

const SEC_CARDS: readonly CardData[] = [
    {
        title: 'Your Guards. Scheduled. Accountable.',
        subtitle: 'Right guard, right licence, right site — every time, without the manual back-and-forth.',
        buttonOne: 'Explore Operations ›',
        buttonTwo: 'Book a demo',
        points: [
            'Shift scheduling across 100+ client sites from one view',
            'Licence and certification checks run before every assignment',
            'Live GPS clock-in and clock-out per guard',
            'Instant escalation for no-shows, late arrivals, and incidents',
        ],
        insideComponent: null,
    },
    {
        title: 'Every Guard. Verified. Every Site.',
        subtitle: 'Background checks, provincial licences, and compliance docs — confirmed before a guard ever steps on site.',
        buttonOne: 'See How It Works ›',
        buttonTwo: 'Book a demo',
        points: [
            'Background checks completed before day one',
            'Provincial licence verification built into every assignment',
            'Centralised credential vault per worker',
            'Auto-hold on workers with expired or missing certifications',
        ],
        insideComponent: null,
    },
    {
        title: 'Close More Security Contracts.',
        subtitle: 'Track every opportunity from cold call to signed agreement — no spreadsheets.',
        buttonOne: 'Explore Sales ›',
        buttonTwo: 'Book a demo',
        points: [
            'Pipeline management built for security staffing sales cycles',
            'Proposal and contract tracking by client site',
            'Win/loss reporting by contract type and region',
            'Auto-renewal alerts so you never lose a long-term account',
        ],
        insideComponent: null,
    },
    {
        title: 'Incident Reports. Automated.',
        subtitle: 'One tap to log, escalate, and close any site incident — with a client-ready audit trail.',
        buttonOne: 'See How It Works ›',
        buttonTwo: 'Book a demo',
        points: [
            'One-tap incident filing from the field',
            'Auto-escalation to supervisors and client contacts',
            'Timestamped, GPS-tagged documentation',
            'Client-ready PDF export in seconds',
        ],
        insideComponent: null,
    },
    {
        title: 'Every Site. One View.',
        subtitle: 'Live guard deployment across every client location — no phone calls, no guesswork.',
        buttonOne: 'See the Dashboard ›',
        buttonTwo: 'Book a demo',
        points: [
            'Real-time coverage map across all active sites',
            'Instant alerts for uncovered posts',
            'Shift-swap approvals handled in seconds',
            'Historical coverage analytics by site and client',
        ],
        insideComponent: null,
    },
    {
        title: 'Audit-Ready. Always.',
        subtitle: 'Compliance documentation compiled automatically — nothing to scramble for at renewal.',
        buttonOne: 'Learn More ›',
        buttonTwo: 'Book a demo',
        points: [
            'Auto-compiled shift and incident logs by site',
            'Licence expiry alerts before they become a problem',
            'One-click audit package generation per client',
            'Shareable compliance reports with client branding',
        ],
        insideComponent: null,
    },
];

const SecurityBentoGrid = (): React.ReactElement => {
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [activeCard, setActiveCard] = useState<CardData>(SEC_CARDS[0]);

    const handleOpenModal = (card: CardData): void => {
        setActiveCard(card);
        setModalOpen(true);
    };

    const handleClose = (): void => {
        setModalOpen(false);
    };

    return (
        <>
            <div className="sec-wrapper">
                <div className="sec-grid">

                    {/* Row 1–2 left: Operations */}
                    <div className="bento-card sec-card-1">
                        <div className="bento-card-image-container"><img src='./bento-images/secguards.jpeg'/></div>
                        <div className="bento-card-text-container">
                            <div className="bento-card-text">
                                <div className="bento-card-title" >{SEC_CARDS[0].title}</div>
                                <div className="bento-card-subtitle" >{SEC_CARDS[0].subtitle}</div>
                            </div>
                            {/* <button className="bento-card-button"><ChevronRight size={20} /></button> */}
                        </div>
                    </div>

                    {/* Row 1–2 center: Compliance hero */}
                    <div className="bento-card sec-card-2">
                        <div className="bento-card-image-container"><img src='./bento-images/securitybento.jpeg'/></div>
                        <div className="bento-card-text-container">
                            <div className="bento-card-text">
                                <div className="bento-card-title" >{SEC_CARDS[1].title}</div>
                                <div className="bento-card-subtitle" >{SEC_CARDS[1].subtitle}</div>
                            </div>
                            {/* <button className="bento-card-button"><ChevronRight size={20} /></button> */}
                        </div>
                    </div>

                    {/* Row 1 right: accent stat */}
                    <div className="bento-card sec-card-3">
                        <div className="bento-card-image-container"><SpeedInteractive /></div>
                        <div className="bento-card-text-container">
                            <div className="bento-card-text">
                                <div className="bento-card-title">{'< 2 hr'}</div>
                                <div className="bento-card-subtitle">Average guard placement time</div>
                            </div>
                        </div>
                    </div>

                    {/* Row 2 right: accent stat */}
                    <div className="bento-card sec-card-4">
                        <div className="bento-card-image-container"><ShieldInteractive/></div>
                        <div className="bento-card-text-container">
                            <div className="bento-card-text">
                                <div className="bento-card-title">99.8%</div>
                                <div className="bento-card-subtitle">Shift compliance rate across all sites</div>
                            </div>
                        </div>
                    </div>

                    {/* Row 3 left: Sales */}
                    <div className="bento-card sec-card-5">
                        <div className="bento-card-image-container"><img style={{transform:'scale(1.07) ', marginTop:'-40px'}} src='./bento-images/dealsclosed.webp'/></div>
                        <div className="bento-card-text-container">
                            <div className="bento-card-text">
                                <div className="bento-card-title">{SEC_CARDS[2].title}</div>
                                <div className="bento-card-subtitle">{SEC_CARDS[2].subtitle}</div>
                            </div>
                            {/* <button className="bento-card-button"><ChevronRight size={20} /></button> */}
                        </div>
                    </div>

                    {/* Row 4 left: accent stat */}
                    <div className="bento-card sec-card-6">
                        <div className="bento-card-image-container"><MoneyInteractive /></div>
                        <div className="bento-card-text-container">
                            <div className="bento-card-text">
                                <div className="bento-card-title">$250M+</div>
                                <div className="bento-card-subtitle">In payroll powered</div>
                            </div>
                        </div>
                    </div>

                    {/* Rows 3–4 center-left: Incident Management */}
                    <div className="bento-card sec-card-7">
                        <div className="bento-card-image-container"><img src='./bento-images/incident.jpg' style={{marginTop:'-5px'}}/></div>
                        <div className="bento-card-text-container">
                            <div className="bento-card-text">
                                <div className="bento-card-title">{SEC_CARDS[3].title}</div>
                                <div className="bento-card-subtitle">{SEC_CARDS[3].subtitle}</div>
                            </div>
                            {/* <button className="bento-card-button"><ChevronRight size={20} /></button> */}
                        </div>
                    </div>

                    {/* Rows 3–4 center-right: Multi-site */}
                    <div className="bento-card sec-card-8">
                        <div className="bento-card-image-container"><img src='./bento-images/healthcare/multiple-facilities.jpg'/></div>
                        <div className="bento-card-text-container">
                            <div className="bento-card-text">
                                <div className="bento-card-title">{SEC_CARDS[4].title}</div>
                                <div className="bento-card-subtitle">{SEC_CARDS[4].subtitle}</div>
                            </div>
                            {/* <button className="bento-card-button"><ChevronRight size={20} /></button> */}
                        </div>
                    </div>

                    {/* Rows 3–4 right: Audit */}
                    <div className="bento-card sec-card-9">
                        <div className="bento-card-image-container"><img src='./bento-images/compliance.png' style={{transform:'scale(1.15) ', marginTop:'-40px'}}/></div>
                        <div className="bento-card-text-container">
                            <div className="bento-card-text">
                                <div className="bento-card-title">{SEC_CARDS[5].title}</div>
                                <div className="bento-card-subtitle">{SEC_CARDS[5].subtitle}</div>
                            </div>
                            {/* <button className="bento-card-button"><ChevronRight size={20} /></button> */}
                        </div>
                    </div>

                </div>
            </div>

            <BentoGridModal open={modalOpen} onClose={handleClose} cardData={activeCard} />
        </>
    );
};

export { SecurityBentoGrid };
